import {
    Elements,
    PaymentElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    RowList,
    Spinner,
    YStack,
} from 'smoothie';

import { STRIPE_PUBLISHABLE_KEY } from '../../config/config';
import {
    useAttachPaymentMethod,
    useCreatePaymentMethodSetupIntent,
} from '../hooks/usePaymentMethod';
import {
    STRIPE_CONFIRM_PARAMS,
    STRIPE_ELEMENTS_FONTS,
    STRIPE_PAYMENT_ELEMENT_FIELDS,
    useStripeElementsAppearance,
} from '../hooks/useStripeElementsAppearance';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY ?? '');

interface PaymentMethodFormProps {
    description?: string;
    submitLabel: string;
    onSuccess: () => void;
}

const PaymentMethodFormContent = ({
    description,
    submitLabel,
    onSuccess,
}: PaymentMethodFormProps) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isPaymentElementComplete, setIsPaymentElementComplete] =
        useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { mutateAsync: attachPaymentMethod } = useAttachPaymentMethod();
    const elementsAppearance = useStripeElementsAppearance({
        isDisabled: isSubmitting,
    });

    useEffect(() => {
        void elements?.update({ appearance: elementsAppearance });
    }, [elements, isSubmitting]);

    const handleSubmit = async () => {
        setErrorMessage(null);
        if (stripe == null || elements == null) {
            return;
        }

        setIsSubmitting(true);
        try {
            const { setupIntent, error } = await stripe.confirmSetup({
                elements,
                confirmParams: STRIPE_CONFIRM_PARAMS,
                redirect: 'if_required',
            });
            const paymentMethodId =
                typeof setupIntent?.payment_method === 'string'
                    ? setupIntent.payment_method
                    : setupIntent?.payment_method?.id;
            if (error != null || paymentMethodId == null) {
                setErrorMessage(
                    error?.message ?? 'Could not confirm card details.',
                );
                return;
            }

            await attachPaymentMethod({ body: { paymentMethodId } });
            onSuccess();
        } catch (err) {
            setErrorMessage(
                err instanceof Error
                    ? err.message
                    : 'Something went wrong. Please try again.',
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <YStack gap={20} width="100%">
            {description != null && (
                <BodyMediumRegular color="$gray1000">
                    {description}
                </BodyMediumRegular>
            )}

            <RowList width="100%" rowHeaderProps={{ title: 'New card' }}>
                <YStack
                    padding={16}
                    pointerEvents={isSubmitting ? 'none' : 'auto'}
                >
                    <PaymentElement
                        options={{
                            layout: 'tabs',
                            fields: STRIPE_PAYMENT_ELEMENT_FIELDS,
                            readOnly: isSubmitting,
                        }}
                        onChange={(event) => {
                            setIsPaymentElementComplete(event.complete);
                        }}
                    />
                </YStack>
            </RowList>

            {errorMessage != null && (
                <BodySmallRegular color="$red1100">
                    {errorMessage}
                </BodySmallRegular>
            )}

            <Button
                text={isSubmitting ? 'Processing…' : submitLabel}
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                disabled={
                    !isPaymentElementComplete || isSubmitting || stripe == null
                }
                onPress={() => {
                    void handleSubmit();
                }}
            />
        </YStack>
    );
};

export const PaymentMethodForm = ({
    description,
    submitLabel,
    onSuccess,
}: PaymentMethodFormProps) => {
    const { mutateAsync: createSetupIntent } =
        useCreatePaymentMethodSetupIntent();
    const elementsAppearance = useStripeElementsAppearance();
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [initError, setInitError] = useState<string | null>(null);

    useEffect(() => {
        createSetupIntent({ body: {} })
            .then(({ clientSecret: secret }) => {
                setClientSecret(secret);
            })
            .catch((err: unknown) => {
                setInitError(
                    err instanceof Error
                        ? err.message
                        : 'Could not load the payment form. Please try again.',
                );
            });
    }, []);

    if (initError != null) {
        return (
            <BodySmallRegular color="$red1100">{initError}</BodySmallRegular>
        );
    }

    if (clientSecret == null) {
        return <Spinner />;
    }

    return (
        <Elements
            stripe={stripePromise}
            options={{
                clientSecret,
                fonts: STRIPE_ELEMENTS_FONTS,
                appearance: elementsAppearance,
            }}
        >
            <PaymentMethodFormContent
                description={description}
                submitLabel={submitLabel}
                onSuccess={onSuccess}
            />
        </Elements>
    );
};

export default PaymentMethodForm;
